import { apiFetch } from "./client";

/**
 * What this student has already been shown, once and never again.
 *
 * TWO THINGS, which is all Lume shows once: the caderno's walkthrough (six
 * tooltips over the real Painel) and the hub's welcome (the screen a student with
 * no caderno lands on). The four screen-by-screen caderno tours and the three
 * hub ones are gone, and so are their flags.
 *
 * The payload is always complete — something the student has never seen comes
 * back `false` rather than missing, so nothing here has to tell "not seen" from
 * "key absent".
 *
 * The keys are the STORED type names, which is why they carry the `caderno_`
 * prefix the URLs and the tour code do not: the backend owns that vocabulary
 * (lib/lume/Onboarding.php) and this is its payload, not ours.
 */
export interface OnboardingStatus {
  caderno_layout: boolean;
  hub_welcome: boolean;
}

/**
 * The two things a student is shown once: the caderno's walkthrough and the
 * hub's welcome. These slugs are what the client sends; the API turns each into
 * a stored type of its own (lib/lume/Onboarding.php).
 */
export type OnboardingScreen = "layout" | "welcome";

/**
 * The status key for a screen.
 *
 * The two vocabularies deliberately differ — a screen is a SLUG ("layout",
 * "welcome"), a status key is a stored type ("caderno_layout", "hub_welcome") —
 * so the translation lives in exactly one place, here, on both sides of the wire.
 */
const STATUS_KEY: Record<OnboardingScreen, keyof OnboardingStatus> = {
  layout: "caderno_layout",
  welcome: "hub_welcome",
};

export function onboardingKeyFor(
  screen: OnboardingScreen
): keyof OnboardingStatus {
  return STATUS_KEY[screen];
}

export function fetchOnboarding(): Promise<OnboardingStatus> {
  return apiFetch<OnboardingStatus>("/api/lume/onboarding/");
}

/**
 * Marks one of them seen and returns the new full status.
 *
 * `screen` is the slug, never a stored type name: the API owns that vocabulary,
 * so nothing the browser sends can reach the table as-is. See the endpoint's
 * docblock for why.
 */
export function markOnboardingSeen(
  screen: OnboardingScreen
): Promise<OnboardingStatus> {
  return apiFetch<OnboardingStatus>("/api/lume/onboarding/", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ screen }),
  });
}
